import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

let tabsInstanceCounter = 0;

/**
 * Tabs Component
 * Wraps Horizon's native <accordion-custom> blocks. Renders as a tab bar on
 * desktop (>= 750px). On mobile shows either an accordion (default) or a
 * horizontal-scrolling tabs strip with prev/next arrows.
 *
 * Improvements over the upstream Icon Lifesaver port:
 * - Empty-panel suppression: panels whose content is blank are hidden so
 *   cheeses without recipes/instructions don't show empty tabs.
 * - Tab nav wraps to multiple lines on desktop instead of horizontal scroll.
 * - Active-colour driven by CSS custom property so a merchant setting can
 *   override the underline + active-tab colour.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * Build an arrow SVG via DOM API so we never inject HTML strings.
 * @param {'prev'|'next'} direction
 */
function buildArrowSvg(direction) {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('width', '14');
  svg.setAttribute('height', '14');
  svg.setAttribute('viewBox', '0 0 14 14');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('aria-hidden', 'true');

  const path = document.createElementNS(SVG_NS, 'path');
  path.setAttribute('d', direction === 'prev' ? 'M8.5 3L5 7L8.5 11' : 'M5.5 3L9 7L5.5 11');
  path.setAttribute('stroke', 'currentColor');
  path.setAttribute('stroke-width', '2');
  path.setAttribute('stroke-linecap', 'round');
  path.setAttribute('stroke-linejoin', 'round');

  svg.appendChild(path);
  return svg;
}

class TabsComponent extends HTMLElement {
  #controller = new AbortController();
  /** Skip animating the programmatic first activation (and re-inits on breakpoint change). */
  #tabInitialised = false;
  /** Removes an in-flight height-transition listener when a new switch interrupts it. */
  #heightCleanup = null;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#applyEmptyPanelSuppression();
    this.#init();

    mediaQueryLarge.addEventListener('change', () => this.#init(), { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
    if (this.#heightCleanup) this.#heightCleanup();
  }

  /**
   * Hide accordion-custom panels whose content is effectively empty.
   * Empty = no media descendants and trimmed text content (excluding the
   * summary heading itself) is blank. Runs once on connect; the customiser
   * re-renders the section on save so dynamic re-checks aren't needed.
   */
  #applyEmptyPanelSuppression() {
    if (this.dataset.hideEmptyPanels === 'false') return;

    const panels = Array.from(this.querySelectorAll('accordion-custom'));
    panels.forEach((panel) => {
      const content = panel.querySelector('.details-content');
      if (!content) return;

      const hasMedia = content.querySelector('img, video, iframe, picture, svg, [data-media]');
      const text = (content.textContent || '').trim();

      if (!hasMedia && text.length === 0) {
        panel.setAttribute('hidden', '');
        panel.dataset.empty = 'true';
      }
    });
  }

  #visiblePanels() {
    return Array.from(this.querySelectorAll('accordion-custom:not([hidden])'));
  }

  #init() {
    const isMobile = isMobileBreakpoint();
    const mobileMode = this.getAttribute('mobile-mode') || 'accordion';

    if (isMobile && mobileMode === 'accordion') {
      this.#showAsAccordion();
    } else {
      this.#showAsTabs();
    }
  }

  #showAsAccordion() {
    const navWrap = this.querySelector('.tabs-navigation-wrap');
    if (navWrap) navWrap.remove();

    this.querySelectorAll('accordion-custom').forEach((acc) => {
      delete acc.dataset.disableOnDesktop;
      acc.classList.remove('is-active');
    });

    this.classList.remove('tabs-mode');
    this.classList.add('accordion-mode');
  }

  #showAsTabs() {
    // Re-entering tabs mode (initial load or breakpoint change) re-runs #switchTab(0)
    // programmatically; don't animate that, only user-driven switches after it.
    this.#tabInitialised = false;
    this.classList.add('tabs-mode');
    this.classList.remove('accordion-mode');

    this.querySelectorAll('accordion-custom').forEach((acc) => {
      acc.dataset.disableOnDesktop = 'true';
    });

    const panels = this.#visiblePanels();
    if (panels.length === 0) return;

    if (panels.length === 1) {
      this.classList.add('single-tab');
      this.#switchTab(0, false);
      return;
    }
    this.classList.remove('single-tab');

    let navWrap = this.querySelector('.tabs-navigation-wrap');
    if (!navWrap) {
      navWrap = this.#buildNavigation(panels);
      const accordionWrap = this.querySelector('.tabs-accordion');
      if (accordionWrap) {
        this.insertBefore(navWrap, accordionWrap);
      } else if (this.firstChild) {
        this.insertBefore(navWrap, this.firstChild);
      } else {
        this.appendChild(navWrap);
      }
    }

    const mobileMode = this.getAttribute('mobile-mode') || 'accordion';
    if (isMobileBreakpoint() && mobileMode === 'tabs') {
      if (!navWrap.querySelector('.tabs-nav-arrow')) {
        this.#addArrowButtons(navWrap);
      }
      this.#initArrowNavigation();
    }

    requestAnimationFrame(() => this.#switchTab(0, false));
  }

  /**
   * @param {HTMLElement[]} panels
   * @returns {HTMLDivElement}
   */
  #buildNavigation(panels) {
    const navWrap = document.createElement('div');
    navWrap.className = 'tabs-navigation-wrap';

    const nav = document.createElement('div');
    nav.className = 'tabs-navigation';
    nav.setAttribute('role', 'tablist');

    panels.forEach((panel, index) => {
      const summary = panel.querySelector('summary');
      if (!summary) return;

      const headingText = Array.from(summary.childNodes)
        .filter((node) => node.nodeType === Node.TEXT_NODE)
        .map((node) => (node.textContent || '').trim())
        .join(' ')
        .trim();

      if (!headingText) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'tabs-button';
      // Apply the preset class so Horizon's preset CSS (h5, paragraph, etc.) styles the button.
      // For 'custom' / 'rte' the class is harmless; the inline --font-* vars take over.
      const preset = this.dataset.tabPreset;
      if (preset && preset !== 'custom' && preset !== 'rte') {
        button.classList.add(preset);
      }
      button.id = `tab-${this.#scopedId(index)}`;
      button.textContent = headingText;
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-controls', `panel-${this.#scopedId(index)}`);
      button.setAttribute('tabindex', index === 0 ? '0' : '-1');
      button.dataset.panelIndex = String(index);

      button.addEventListener('click', () => this.#switchTab(index, true), {
        signal: this.#controller.signal,
      });

      nav.appendChild(button);

      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('id', `panel-${this.#scopedId(index)}`);
      panel.setAttribute('aria-labelledby', button.id);
    });

    navWrap.appendChild(nav);

    const liveRegion = document.createElement('div');
    liveRegion.className = 'visually-hidden';
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.dataset.tabsStatus = '';
    navWrap.appendChild(liveRegion);

    return navWrap;
  }

  /**
   * Per-instance ID prefix so multiple tabs blocks on a page don't collide.
   * @param {string|number} suffix
   */
  #scopedId(suffix) {
    if (!this.dataset.scope) {
      this.dataset.scope = `tabs-${++tabsInstanceCounter}`;
    }
    return `${this.dataset.scope}-${suffix}`;
  }

  /**
   * @param {HTMLElement} navWrap
   */
  #addArrowButtons(navWrap) {
    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'tabs-nav-arrow tabs-nav-arrow--prev';
    prev.setAttribute('aria-label', this.dataset.prevLabel || 'Previous tab');
    prev.hidden = true;
    prev.appendChild(buildArrowSvg('prev'));

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'tabs-nav-arrow tabs-nav-arrow--next';
    next.setAttribute('aria-label', this.dataset.nextLabel || 'Next tab');
    next.hidden = true;
    next.appendChild(buildArrowSvg('next'));

    navWrap.appendChild(prev);
    navWrap.appendChild(next);
  }

  #initArrowNavigation() {
    const { signal } = this.#controller;
    const nav = this.querySelector('.tabs-navigation');
    const prev = this.querySelector('.tabs-nav-arrow--prev');
    const next = this.querySelector('.tabs-nav-arrow--next');
    if (!nav) return;

    if (prev) prev.addEventListener('click', () => this.#navigateTabs('prev'), { signal });
    if (next) next.addEventListener('click', () => this.#navigateTabs('next'), { signal });

    nav.addEventListener('scroll', () => this.#updateArrows(), { passive: true, signal });
    window.addEventListener('resize', () => this.#updateArrows(), { passive: true, signal });

    requestAnimationFrame(() => this.#updateArrows());
  }

  /** @param {'prev'|'next'} direction */
  #navigateTabs(direction) {
    const buttons = Array.from(this.querySelectorAll('.tabs-button'));
    const activeButton = buttons.find((btn) => btn.classList.contains('is-active'));
    if (!activeButton || buttons.length === 0) return;

    const currentIndex = buttons.indexOf(activeButton);
    const newIndex =
      direction === 'next'
        ? currentIndex < buttons.length - 1 ? currentIndex + 1 : 0
        : currentIndex > 0 ? currentIndex - 1 : buttons.length - 1;

    this.#switchTab(newIndex, true);
    this.#scrollToActiveTab(newIndex);
  }

  /** @param {number} index */
  #scrollToActiveTab(index) {
    const nav = this.querySelector('.tabs-navigation');
    const buttons = Array.from(this.querySelectorAll('.tabs-button'));
    const activeButton = buttons[index];
    if (!nav || !activeButton) return;

    const buttonRect = activeButton.getBoundingClientRect();
    const navRect = nav.getBoundingClientRect();
    const scrollLeft = nav.scrollLeft + (buttonRect.left - navRect.left) - (navRect.width / 2) + (buttonRect.width / 2);
    nav.scrollTo({ left: scrollLeft, behavior: 'smooth' });
  }

  #updateArrows() {
    const navWrap = this.querySelector('.tabs-navigation-wrap');
    const nav = this.querySelector('.tabs-navigation');
    const prev = this.querySelector('.tabs-nav-arrow--prev');
    const next = this.querySelector('.tabs-nav-arrow--next');
    if (!nav || !navWrap) return;

    const { scrollLeft, scrollWidth, clientWidth } = nav;
    const isAtStart = scrollLeft <= 5;
    const isAtEnd = scrollLeft >= scrollWidth - clientWidth - 5;
    const canScroll = scrollWidth > clientWidth;

    navWrap.classList.toggle('fade-left', !isAtStart && canScroll);
    navWrap.classList.toggle('fade-right', !isAtEnd && canScroll);

    if (prev instanceof HTMLElement) prev.hidden = isAtStart || !canScroll;
    if (next instanceof HTMLElement) next.hidden = isAtEnd || !canScroll;
  }

  /**
   * @param {number} index
   * @param {boolean} shouldFocus
   */
  #switchTab(index, shouldFocus = false) {
    const panels = this.#visiblePanels();
    const buttons = Array.from(this.querySelectorAll('.tabs-button'));
    if (index < 0 || index >= panels.length) return;

    const container = this.querySelector('.tabs-accordion');
    const animate = this.#tabInitialised && container instanceof HTMLElement && this.#dynamicHeightEnabled();
    const startHeight = animate ? container.offsetHeight : 0;

    panels.forEach((panel, i) => {
      const details = panel.querySelector('details');
      const isActive = i === index;
      panel.classList.toggle('is-active', isActive);
      if (details instanceof HTMLDetailsElement) {
        details.open = isActive;
      }
    });

    if (animate) this.#animateHeight(container, startHeight);
    this.#tabInitialised = true;

    let activeButton = null;
    buttons.forEach((button, i) => {
      const isActive = i === index;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', String(isActive));
      button.setAttribute('tabindex', isActive ? '0' : '-1');
      if (isActive) {
        activeButton = button;
        if (shouldFocus) button.focus();
      }
    });

    const liveRegion = this.querySelector('[data-tabs-status]');
    if (liveRegion && activeButton) {
      const suffix = this.dataset.tabSelectedSuffix || 'tab selected';
      liveRegion.textContent = `${activeButton.textContent} ${suffix}`;
    }

    const nav = this.querySelector('.tabs-navigation');
    if (nav && activeButton) {
      requestAnimationFrame(() => {
        const navRect = nav.getBoundingClientRect();
        const buttonRect = activeButton.getBoundingClientRect();
        nav.style.setProperty('--underline-left', `${buttonRect.left - navRect.left}px`);
        nav.style.setProperty('--underline-width', `${buttonRect.width}px`);
      });
    }

    const mobileMode = this.getAttribute('mobile-mode') || 'accordion';
    if (isMobileBreakpoint() && mobileMode === 'tabs') {
      requestAnimationFrame(() => {
        this.#updateArrows();
        this.#scrollToActiveTab(index);
      });
    }
  }

  /** Dynamic height is on only in tabs mode, when the merchant enabled it, and motion is allowed. */
  #dynamicHeightEnabled() {
    return (
      this.dataset.dynamicHeight === 'true' &&
      this.classList.contains('tabs-mode') &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  /**
   * Animate the panel container from the outgoing panel's height to the incoming one.
   * Panels are swapped by the caller first; this measures the new natural height and
   * transitions from startHeight to it. Interrupting a live transition re-targets smoothly.
   * @param {HTMLElement} container - .tabs-accordion
   * @param {number} startHeight - the container height captured before the panel swap
   */
  #animateHeight(container, startHeight) {
    if (this.#heightCleanup) this.#heightCleanup();

    container.style.height = 'auto';
    const endHeight = container.offsetHeight;
    if (endHeight === startHeight) {
      container.style.height = '';
      return;
    }

    container.style.overflow = 'hidden';
    container.style.height = `${startHeight}px`;
    void container.offsetHeight; // force reflow so the start height is registered before transitioning
    container.style.height = `${endHeight}px`;

    const onEnd = (event) => {
      // Ignore transitionend bubbling up from panel children (e.g. an accordion row); only the container's own height transition ends the animation.
      if (event.target !== event.currentTarget || event.propertyName !== 'height') return;
      container.style.height = '';
      container.style.overflow = '';
      container.removeEventListener('transitionend', onEnd);
      this.#heightCleanup = null;
    };
    container.addEventListener('transitionend', onEnd);
    this.#heightCleanup = () => {
      container.removeEventListener('transitionend', onEnd);
      this.#heightCleanup = null;
    };
  }

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    if (!this.classList.contains('tabs-mode') || this.classList.contains('single-tab')) return;

    const buttons = Array.from(this.querySelectorAll('.tabs-button'));
    const activeButton = buttons.find((btn) => btn.getAttribute('aria-selected') === 'true');
    if (!activeButton) return;
    const currentIndex = buttons.indexOf(activeButton);
    if (currentIndex === -1) return;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.#switchTab(currentIndex > 0 ? currentIndex - 1 : buttons.length - 1, true);
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.#switchTab(currentIndex < buttons.length - 1 ? currentIndex + 1 : 0, true);
        break;
      case 'Home':
        event.preventDefault();
        this.#switchTab(0, true);
        break;
      case 'End':
        event.preventDefault();
        this.#switchTab(buttons.length - 1, true);
        break;
    }
  };
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', TabsComponent);
}
